import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery, pyrotchBaseUrl } from "../axios/AxiosInstance";

export interface ChatConversationResponse {
  id: number;
  title?: string;
  description?: string;
  conversationType: "DIRECT" | "GROUP" | "PROJECT" | "DEPARTMENT";
  projectId?: number;
  groupIconUrl?: string;
  archived: boolean;
  muted: boolean;
  lastMessagePreview?: string;
  lastMessageAt?: string;
  unreadCount: number;
  participantCount: number;
  participants?: Array<{
    userId: number;
    name?: string;
    email?: string;
    profilePhoto?: string;
    admin: boolean;
    muted: boolean;
    archived: boolean;
    lastReadMessageId?: number;
    lastReadAt?: string;
  }>;
}

export interface ChatMessageResponse {
  id: number;
  conversationId: number;
  senderId: number;
  senderName?: string;
  senderPhoto?: string;
  messageType: "TEXT" | "IMAGE" | "FILE" | "SYSTEM";
  content?: string;
  replyToMessageId?: number;
  forwardedFromMessageId?: number;
  projectId?: number;
  taskId?: number;
  milestoneId?: number;
  approvalRequestId?: number;
  actionItemTaskId?: number;
  pinned: boolean;
  important: boolean;
  decision: boolean;
  announcement: boolean;
  edited: boolean;
  deleted: boolean;
  mentionedUserIds?: string;
  sentAt?: string;
  createdDate?: string;
  lastModifiedDate?: string;
  attachments?: Array<{
    id: number;
    fileName?: string;
    fileType?: string;
    fileSizeBytes?: number;
    storageKey?: string;
    previewUrl?: string;
    versionLabel?: string;
  }>;
  reactions?: Array<{ emoji: string; count: number; reactedByCurrentUser: boolean }>;
  replyCount?: number;
  taggedReferences?: Array<{ type: "PROJECT" | "PRODUCTION_FILE"; id: number; label: string }>;
}

export const ChatApi = createApi({
  baseQuery: axiosBaseQuery({
    baseUrl: pyrotchBaseUrl + "v1/",
  }),
  reducerPath: "chatApi",
  tagTypes: ["ChatConversation", "ChatMessage"],
  endpoints: (build) => ({
    getChatConversations: build.query<
      { content: ChatConversationResponse[]; totalElements?: number; totalPages?: number; number?: number },
      { page: number; size: number; search?: string; type?: string; archived?: boolean }
    >({
      query: ({ page, size, search = "", type = "", archived = false }) => ({
        url: `chat/conversations?page=${page}&size=${size}&search=${encodeURIComponent(search)}&type=${encodeURIComponent(type)}&archived=${archived}`,
        method: "GET",
      }),
      providesTags: ["ChatConversation"],
    }),
    createChatConversation: build.mutation<ChatConversationResponse, any>({
      query: (data) => ({
        url: "chat/conversations",
        method: "POST",
        data,
      }),
      invalidatesTags: ["ChatConversation"],
    }),
    getChatMessages: build.query<
      { content: ChatMessageResponse[]; totalElements?: number; totalPages?: number; number?: number },
      { conversationId: number; page: number; size: number; search?: string }
    >({
      query: ({ conversationId, page, size, search = "" }) => ({
        url: `chat/conversations/${conversationId}/messages?page=${page}&size=${size}&search=${encodeURIComponent(search)}`,
        method: "GET",
      }),
      providesTags: ["ChatMessage"],
    }),
    sendChatMessage: build.mutation<ChatMessageResponse, { conversationId: number; payload: any }>({
      query: ({ conversationId, payload }) => ({
        url: `chat/conversations/${conversationId}/messages`,
        method: "POST",
        data: payload,
      }),
      invalidatesTags: ["ChatMessage", "ChatConversation"],
    }),
    markChatConversationRead: build.mutation<ChatConversationResponse, { conversationId: number }>({
      query: ({ conversationId }) => ({
        url: `chat/conversations/${conversationId}/read`,
        method: "PUT",
      }),
      invalidatesTags: ["ChatConversation"],
    }),
    archiveChatConversation: build.mutation<ChatConversationResponse, { conversationId: number; archived: boolean }>({
      query: ({ conversationId, archived }) => ({
        url: `chat/conversations/${conversationId}/archive?archived=${archived}`,
        method: "PUT",
      }),
      invalidatesTags: ["ChatConversation"],
    }),
    sendChatMessageWithAttachments: build.mutation<
      ChatMessageResponse,
      { conversationId: number; content?: string; files: File[]; taggedReferences?: Array<{ type: string; id: number }> }
    >({
      query: ({ conversationId, content, files, taggedReferences }) => {
        const formData = new FormData();
        if (content) formData.append("content", content);
        if (taggedReferences && taggedReferences.length > 0) formData.append("taggedReferences", JSON.stringify(taggedReferences));
        files.forEach((file) => formData.append("files", file));
        return {
          url: `chat/conversations/${conversationId}/messages/attachments`,
          method: "POST",
          data: formData,
        };
      },
      invalidatesTags: ["ChatMessage", "ChatConversation"],
    }),
    editChatMessage: build.mutation<ChatMessageResponse, { messageId: number; content: string }>({
      query: ({ messageId, content }) => ({
        url: `chat/messages/${messageId}`,
        method: "PUT",
        data: { content },
      }),
      invalidatesTags: ["ChatMessage"],
    }),
    deleteChatMessage: build.mutation<void, { messageId: number }>({
      query: ({ messageId }) => ({
        url: `chat/messages/${messageId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChatMessage"],
    }),
    pinChatMessage: build.mutation<ChatMessageResponse, { messageId: number; pinned: boolean }>({
      query: ({ messageId, pinned }) => ({
        url: `chat/messages/${messageId}/pin?pinned=${pinned}`,
        method: "PUT",
      }),
      invalidatesTags: ["ChatMessage"],
    }),
    toggleChatReaction: build.mutation<ChatMessageResponse, { messageId: number; emoji: string }>({
      query: ({ messageId, emoji }) => ({
        url: `chat/messages/${messageId}/reactions`,
        method: "POST",
        data: { emoji },
      }),
      invalidatesTags: ["ChatMessage"],
    }),
    getChatMessageReplies: build.query<
      { content: ChatMessageResponse[]; totalElements?: number; totalPages?: number; number?: number },
      { messageId: number; page: number; size: number }
    >({
      query: ({ messageId, page, size }) => ({
        url: `chat/messages/${messageId}/replies?page=${page}&size=${size}`,
        method: "GET",
      }),
      providesTags: ["ChatMessage"],
    }),
    deleteChatConversation: build.mutation<void, { conversationId: number }>({
      query: ({ conversationId }) => ({
        url: `chat/conversations/${conversationId}/delete-for-me`,
        method: "PUT",
      }),
      invalidatesTags: ["ChatConversation"],
    }),
    forwardChatMessage: build.mutation<ChatMessageResponse[], { messageId: number; targetConversationIds: number[] }>({
      query: ({ messageId, targetConversationIds }) => ({
        url: `chat/messages/${messageId}/forward`,
        method: "POST",
        data: { targetConversationIds },
      }),
      invalidatesTags: ["ChatMessage", "ChatConversation"],
    }),
    addChatMembers: build.mutation<ChatConversationResponse, { conversationId: number; userIds: number[] }>({
      query: ({ conversationId, userIds }) => ({
        url: `chat/conversations/${conversationId}/members`,
        method: "POST",
        data: userIds,
      }),
      invalidatesTags: ["ChatConversation"],
    }),
  }),
});

export const {
  useLazyGetChatConversationsQuery,
  useLazyGetChatMessagesQuery,
  useCreateChatConversationMutation,
  useSendChatMessageMutation,
  useSendChatMessageWithAttachmentsMutation,
  useMarkChatConversationReadMutation,
  useArchiveChatConversationMutation,
  useEditChatMessageMutation,
  useDeleteChatMessageMutation,
  usePinChatMessageMutation,
  useToggleChatReactionMutation,
  useLazyGetChatMessageRepliesQuery,
  useDeleteChatConversationMutation,
  useForwardChatMessageMutation,
  useAddChatMembersMutation,
} = ChatApi;
